/**
 * Sprint 5 (2026-06-25): przepisany search handler na mikroserwis search.kablowo.pl.
 *
 * Zmiany vs poprzednia wersja:
 * - endpoint: search.kablowo.pl/api/v1/suggest (zamiast /wp-json/kablowo/v1/search)
 * - debounce 200 ms na keystroke (anty race condition)
 * - AbortController cancellation poprzedniego fetch
 * - sessionStorage L1 cache (klucz=normalized_q, TTL 5 min)
 * - mapping price_html → price (kompatybilne ze starym template)
 * - bez X-WP-Nonce (mikroserwis ma własną auth — Sprint 6 doda HMAC token)
 */

const SS_PREFIX = 'kbl_search_v2:';
const SS_TTL_MS = 5 * 60 * 1000; // 5 min
const DEBOUNCE_MS = 200;

// Sprint 6: HMAC token fetchowany z /wp-json/kablowo/v1/search-token, cache 50 min.
const TOKEN_STORAGE_KEY = 'kbl_search_token';
const TOKEN_TTL_MS = 50 * 60 * 1000; // 50 min (token ma 1h TTL, bufor na time-bucket flip)
let _tokenInflightPromise = null;

async function getSearchToken() {
    // sessionStorage cache
    try {
        const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
        if (raw) {
            const o = JSON.parse(raw);
            if (Date.now() - o.t < TOKEN_TTL_MS && o.v) {
                return o.v;
            }
        }
    } catch {
        // ignore
    }
    // Dedup concurrent requests
    if (_tokenInflightPromise) return _tokenInflightPromise;

    _tokenInflightPromise = (async () => {
        try {
            const r = await fetch('/wp-json/kablowo/v1/search-token', {
                method: 'GET',
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
            });
            if (!r.ok) throw new Error('token http ' + r.status);
            const data = await r.json();
            if (data && data.token) {
                try {
                    sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({ t: Date.now(), v: data.token }));
                } catch { /* QuotaExceeded */ }
                return data.token;
            }
        } catch (e) {
            console.warn('[kablowo-search] token fetch failed:', e.message);
        } finally {
            _tokenInflightPromise = null;
        }
        return '';
    })();

    return _tokenInflightPromise;
}

function normalizeQuery(q) {
    return (q || '').normalize('NFC').toLowerCase().replace(/\s+/g, ' ').trim();
}

function sessionGet(key) {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const o = JSON.parse(raw);
        if (Date.now() - o.t > SS_TTL_MS) {
            sessionStorage.removeItem(key);
            return null;
        }
        return o.v;
    } catch {
        return null;
    }
}

function sessionSet(key, value) {
    try {
        sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value }));
    } catch {
        // QuotaExceeded — ignoruj
    }
}

function mapHits(hits) {
    // Mikroserwis: {id, title, url, image, price_html, brand, type}
    // Template:   {id, title, url, image, price (html), brand}
    return (hits || []).map((h) => ({
        id: h.id,
        title: h.title || '',
        url: h.url || '',
        image: h.image || '',
        brand: h.brand || '',
        price: h.price_html || '',
        type: h.type || 'simple',
    }));
}

export function searchData() {
    return {
        query: '',
        results: [],
        estimatedTotal: 0,
        isLoading: false,
        showSuggestions: false,
        activeResultIndex: -1,
        _debounceTimer: null,
        _abortController: null,

        shouldShowSuggestions() {
            return this.query.length >= 2 && this.results.length > 0;
        },
        hasResults() {
            return this.results.length > 0;
        },
        hasNoMatches() {
            return this.query.length >= 2 && this.results.length === 0 && !this.isLoading;
        },

        init() {
            this.$watch('query', (value) => {
                clearTimeout(this._debounceTimer);

                if (!value || value.length < 2) {
                    this.results = [];
                    this.showSuggestions = false;
                    this.isLoading = false;
                    if (this._abortController) this._abortController.abort();
                    return;
                }

                this._debounceTimer = setTimeout(() => this.fetchResults(value), DEBOUNCE_MS);
            });

            document.addEventListener('click', (event) => {
                if (!event.target.closest('.search-wrapper') && !event.target.closest('.mobile-search-overlay')) {
                    this.showSuggestions = false;
                }
            });

            // Keyboard: ESC zamyka, ↑↓ nawigacja, Enter wybiera
            this.$el.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    this.showSuggestions = false;
                    return;
                }
                if (!this.showSuggestions || this.results.length === 0) return;

                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    this.activeResultIndex = Math.min(this.activeResultIndex + 1, this.results.length - 1);
                } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    this.activeResultIndex = Math.max(this.activeResultIndex - 1, -1);
                } else if (event.key === 'Enter' && this.activeResultIndex >= 0) {
                    event.preventDefault();
                    const r = this.results[this.activeResultIndex];
                    if (r) this.selectResult(r.url);
                }
            });
        },

        async fetchResults(query) {
            if (!query || query.length < 2) return;

            const normalized = normalizeQuery(query);
            const cacheKey = SS_PREFIX + normalized;

            // L1: sessionStorage cache
            const cached = sessionGet(cacheKey);
            if (cached) {
                this.results = cached.hits || cached; // backwards compat: old cache pamiętał tablicę
                this.estimatedTotal = cached.estimatedTotal || (cached.hits ? cached.hits.length : cached.length);
                this.showSuggestions = this.results.length > 0;
                this.activeResultIndex = -1;
                this.isLoading = false;
                return;
            }

            // Anuluj poprzedni inflight fetch
            if (this._abortController) this._abortController.abort();
            this._abortController = new AbortController();

            this.isLoading = true;

            try {
                const apiUrl = (window.kablowoSearch?.apiUrl || 'https://search.kablowo.pl/api/v1').replace(/\/$/, '');
                const url = `${apiUrl}/suggest?q=${encodeURIComponent(query)}&limit=10`;

                // Sprint 6: HMAC token w X-Kablowo-Token header.
                const token = await getSearchToken();
                const headers = { Accept: 'application/json' };
                if (token) headers['X-Kablowo-Token'] = token;

                const response = await fetch(url, {
                    method: 'GET',
                    signal: this._abortController.signal,
                    headers,
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();
                const hits = mapHits(data.hits || []);
                const estimatedTotal = data.estimated_total || hits.length;

                this.results = hits;
                this.estimatedTotal = estimatedTotal;
                this.showSuggestions = hits.length > 0 || (this.query && this.query.length >= 2);
                this.activeResultIndex = -1;
                sessionSet(cacheKey, { hits, estimatedTotal });

                // Sprint 6.3: log autocomplete query do wp_kablowo_search_log.
                // Skip jeśli admin sprawdza wyniki przez link z panelu (kbl_admin_check=1).
                try {
                    const isAdminCheck = window.location.search.indexOf('kbl_admin_check=1') >= 0;
                    if (!isAdminCheck && query && query.length >= 2 && navigator.sendBeacon) {
                        const payload = JSON.stringify({ q: query, results_count: estimatedTotal });
                        const blob = new Blob([payload], { type: 'application/json' });
                        navigator.sendBeacon('/wp-json/kablowo/v1/log-search', blob);
                    }
                } catch (e) {
                    // ignore — logging best-effort
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Search error:', error);
                    this.results = [];
                }
            } finally {
                this.isLoading = false;
                this._abortController = null;
            }
        },

        submitSearch(event) {
            if (this.results && this.results.length === 1) {
                event.preventDefault();
                window.location.href = this.results[0].url;
                return false;
            }
            if (this.query && this.query.length > 0) {
                return true;
            }
            event.preventDefault();
            return false;
        },

        submitForm() {
            if (this.query && this.query.length > 0) {
                const form = this.$root.querySelector('form');
                if (form) {
                    form.submit();
                } else {
                    window.location.href = '/?s=' + encodeURIComponent(this.query) + '&post_type=product';
                }
            }
        },

        selectResult(url) {
            window.location.href = url;
        },

        // Mobile overlay helpers
        openMobileOverlay() {
            this.showSuggestions = true;
            document.body.classList.add('search-overlay-open');
            this.$nextTick(() => {
                const input = document.querySelector('.mobile-search-overlay__input');
                if (input) input.focus();
            });
        },

        closeMobileOverlay() {
            this.showSuggestions = false;
            document.body.classList.remove('search-overlay-open');
        },
    };
}

if (typeof window !== 'undefined') {
    window.searchData = searchData;

    if (window.Alpine) {
        window.Alpine.data('searchData', searchData);
    } else {
        document.addEventListener('alpine:init', () => {
            window.Alpine.data('searchData', searchData);
        });
    }
}
